/* eslint-disable no-restricted-syntax */
import { Injectable } from '@jack-henry/frontend-utils/di';
import { TmHttpClient } from '@treasury/core/http';
import { dateDistance } from '@treasury/utils/functions';
import { AccountDto, AccountsGroupDto } from '../../types';
import { DashboardProduct } from '../../types/dashboard/dashboard';
import { Notification, notificationsDto } from '../../types/notifications.dto';
import { PaymentTypes } from '../../types/payment';
import { CutoffTimesDto } from '../../types/processing-time.dto';

const dollarUSLocale = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

export type PaymentType = 'transfer' | 'loanpayment' | 'loanPayment' | 'wire' | 'ach' | 'stp';

@Injectable()
export class DashboardService {
    constructor(private readonly http: TmHttpClient) {}

    public async getMyAccounts() {
        const response = await this.http.request<AccountsGroupDto[]>(`dashboard/myaccounts`, {
            method: 'GET',
        });

        response.map((accountGroup: AccountsGroupDto) =>
            accountGroup.accounts.forEach((account: AccountDto) => {
                account.currentBalanceFormatted = dollarUSLocale.format(account.currentBalance);
                account.availableBalanceFormatted = dollarUSLocale.format(account.availableBalance);
                account.collectedBalanceFormatted = dollarUSLocale.format(account.collectedBalance);
            })
        );
        return response;
    }

    public getPendingApprovalPaymentsByType(type: PaymentTypes) {
        return this.http.request(`dashboard/paymentPendingApproval?type=${type}`, {
            method: 'GET',
        });
    }

    // action types:
    // Approve Transfer
    // Approve Loan Payment
    // Approve WIRE Payment
    // Approve ACH Payment
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public approveOrRejectPayment(items: any[], type: PaymentType) {
        let actionType;
        switch (type) {
            case 'transfer':
                actionType = 'Approve Transfer';
                break;
            case 'loanpayment':
                actionType = 'Approve Loan Payment';
                break;
            case 'wire':
                actionType = 'Approve WIRE Payment';
                break;
            case 'ach':
                actionType = 'Approve ACH Payment';
                break;
            default:
                break;
        }
        const body = {
            batch: items.map(item => ({ key: item.id, value: item.selectedState })),
            securityMessage: {
                actionType,
                status: null,
                methodUsed: null,
                hasAlternate: false,
                errorCode: null,
                message: null,
                oneTimePassword: null,
            },
        };
        return this.http.request(`dashboard/batch?type=${type}`, {
            method: 'POST',
            body,
        });
    }

    public logNotificationActivity() {
        return this.http.request(`navigations/auditUserActivity/notifications`, {
            method: 'POST',
        });
    }

    public async markNotificationsAsRead(notificationIds: { id: number }[]) {
        return this.http.request(`notifications/setRead?value=true`, {
            method: 'PUT',
            body: notificationIds,
        });
    }

    public async archiveNotifications(notificationIds: { id: number }[]) {
        return this.http.request(`notifications/archive`, {
            method: 'PUT',
            body: notificationIds,
        });
    }

    public async getRecentNotifications() {
        const response = await this.http.request<notificationsDto>(
            `notifications/recent?html=true`,
            {
                method: 'GET',
            }
        );

        return {
            notifications: response.topMessages.map((notification: Notification) => ({
                ...notification,
                createdOn: `${dateDistance(new Date(notification.createdOn), new Date())} ago`,
            })),
            count: response.totalUnreadCount,
        };
    }

    public async getCutoffTimes() {
        const timeConversion = (time: string) => {
            let formattedTime: string[] = time
                .toString()
                .match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];
            if (formattedTime.length > 1) {
                formattedTime = formattedTime.slice(1);
                formattedTime[4] = +formattedTime[0] < 12 ? ' AM' : ' PM';
                formattedTime[0] = (+formattedTime[0] % 12 || 12).toString();
            }

            formattedTime.splice(3, 1);
            return formattedTime.join('');
        };

        const response = await this.http.request<CutoffTimesDto>(`companyaccounts/cutoffTimes`, {
            method: 'GET',
        });
        const cutoffTimes = response.processingCutoffs.map(
            (cutoff: { productType: string; cutoffTime: string }) => ({
                cutoffTime: timeConversion(cutoff.cutoffTime),
                label: cutoff.productType,
            })
        );

        return {
            cutoffTimes,
            timeZone: response.timeZone,
        };
    }

    public async getAllMyAccounts() {
        return this.http.request(`dashboard/myaccounts`, {
            method: 'GET',
        });
    }

    public async getProducts() {
        return this.http.request(`dashboard/getProducts`, {
            method: 'GET',
        }) as unknown as DashboardProduct[];
    }
}
